import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function validateDimensions(width: number, height: number) {
  return {
    width: Math.max(2000, Math.min(4000, width)),
    height: Math.max(1800, Math.min(4500, height)),
  }
}

function shiftComponentsRight(model: ModelData, componentIds: string[], widthDifference: number) {
  componentIds.forEach((componentId) => {
    const component = getParam(model.parameters!, componentId)
    if (component?.position) {
      updateParam(model, componentId, {
        position: {
          ...component.position,
          x: (component.position.x || 0) + widthDifference,
        } as ModelParameter['position'],
      })
    }
  })
}

function updateDoorHandles(model: ModelData, doorPositionId: string, newDoorHeight: number) {
  const door = getParam(model.parameters!, doorPositionId)
  if (!door?.hardware?.handles || door.hardware.handles.length === 0) return
  const currentDoorHeight = door.height
  if (!currentDoorHeight || currentDoorHeight === newDoorHeight) return

  const updatedHandles = door.hardware.handles.map((handle) => {
    const relativeY = handle.position.y / currentDoorHeight
    const newY = Math.max(50, Math.min(newDoorHeight - (handle.height || 200) - 50, relativeY * newDoorHeight))
    return { ...handle, position: { ...handle.position, y: Math.round(newY) } }
  })
  updateParam(model, doorPositionId, { hardware: { ...door.hardware, handles: updatedHandles } })
}

function recalculatePositions(model: ModelData, recalculateHeights = false) {
  const frameThickness = 60
  const totalHeight = model.height as number

  const sections = {
    leftFixed: getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 550,
    doorSection: getParam(model.parameters!, 'door_1_bottom_frame_profile')?.width || 900,
    rightFixed: getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 550,
  }

  const positions = {
    leftFixed: 0,
    doorSection: sections.leftFixed + frameThickness,
    rightFixed: sections.leftFixed + frameThickness + sections.doorSection + frameThickness,
  }

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')

  const transomH = (topFrame?.height || 60) + (doorTopGlassPanel?.height || 200) + (middleHorizontalFrame?.height || 60)
  const doorTopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
  const doorYStart = transomH
  const bottomY = totalHeight - (topFrame?.height || 60)

  const currentVerticalFrameHeight = recalculateHeights
    ? totalHeight - (topFrame?.height || 60)
    : getParam(model.parameters!, 'left_vertical_frame_profile')?.height || totalHeight - (topFrame?.height || 60)

  const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
  const doorBottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const doorMainPanelHeight = bottomY - doorYStart + (doorTopFrame?.height || 60)

  const currentDoorFrameHeight = recalculateHeights
    ? door1LeftFrame?.height !== undefined ? door1LeftFrame.height : doorMainPanelHeight
    : door1LeftFrame?.height || doorMainPanelHeight

  const doorGlassPanel = getParam(model.parameters!, 'door_glass_panel')
  const currentDoorGlassHeight = recalculateHeights
    ? doorGlassPanel?.height !== undefined
      ? doorGlassPanel.height
      : currentDoorFrameHeight - (doorTopFrame?.height || 60) - (doorBottomFrame?.height || 60)
    : doorGlassPanel?.height || 1760

  const currentFixedPanelHeight = recalculateHeights
    ? totalHeight - frameThickness * 2
    : getParam(model.parameters!, 'left_glass_panel')?.height || totalHeight - frameThickness * 2

  const calculatedTotalWidth = sections.leftFixed + sections.doorSection + sections.rightFixed + frameThickness * 2
  const finalTotalWidth = (model.width as number) || calculatedTotalWidth

  if (!model.width) {
    model.width = finalTotalWidth
  }

  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })
  updateParam(model, 'right_frame_profile', { position: { x: finalTotalWidth - frameThickness, y: 0, z: 0 } })

  const bottomFrameY = recalculateHeights ? bottomY : getParam(model.parameters!, 'bottom_left_frame_profile')?.position?.y || bottomY

  updateParam(model, 'bottom_left_frame_profile', { position: { x: positions.leftFixed, y: bottomFrameY, z: 0 } })
  updateParam(model, 'door_1_bottom_frame_profile', { position: { x: positions.doorSection, y: bottomFrameY, z: 0 } })
  updateParam(model, 'bottom_right_frame_profile', { position: { x: positions.rightFixed, y: bottomFrameY, z: 0 } })

  updateParam(model, 'left_vertical_frame_profile', {
    position: { x: positions.doorSection - frameThickness, y: frameThickness, z: 0 },
    height: currentVerticalFrameHeight,
  })
  updateParam(model, 'right_vertical_frame_profile', {
    position: { x: positions.rightFixed - frameThickness, y: frameThickness, z: 0 },
    height: currentVerticalFrameHeight,
  })

  const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
  const preservedDoor1LeftY = recalculateHeights ? door1LeftFrame?.position?.y : doorYStart
  const preservedDoor1RightY = recalculateHeights ? door1RightFrame?.position?.y : doorYStart

  updateParam(model, 'door_1_left_frame_profile', {
    position: { x: positions.doorSection, y: preservedDoor1LeftY ?? doorYStart, z: 0 },
    height: currentDoorFrameHeight,
  })
  updateParam(model, 'door_1_right_frame_profile', {
    position: { x: positions.doorSection + sections.doorSection - frameThickness, y: preservedDoor1RightY ?? doorYStart, z: 0 },
    height: currentDoorFrameHeight,
  })

  const preservedDoor1TopY = recalculateHeights
    ? doorTopFrame?.position?.y
    : doorYStart - (middleHorizontalFrame?.height || 60)

  updateParam(model, 'door_1_top_frame_profile', {
    position: { x: positions.doorSection, y: preservedDoor1TopY ?? doorYStart - (middleHorizontalFrame?.height || 60), z: 0 },
    width: sections.doorSection,
  })

  updateParam(model, 'middle_horizontal_frame_profile', {
    position: { x: positions.doorSection, y: (topFrame?.height || 60) + (doorTopGlassPanel?.height || 200), z: 0 },
    width: sections.doorSection,
  })

  updateParam(model, 'left_glass_panel', {
    position: { x: frameThickness, y: frameThickness, z: 0 },
    width: sections.leftFixed - frameThickness,
    height: currentFixedPanelHeight,
  })

  updateParam(model, 'right_glass_panel', {
    position: { x: positions.rightFixed + frameThickness, y: frameThickness, z: 0 },
    width: sections.rightFixed - frameThickness * 2,
    height: currentFixedPanelHeight,
  })

  updateParam(model, 'door_top_glass_panel', {
    position: { x: positions.doorSection + frameThickness, y: frameThickness, z: 0 },
    width: sections.doorSection - frameThickness,
  })

  const preservedDoorGlassPanelY = recalculateHeights
    ? doorGlassPanel?.position?.y
    : doorGlassPanel?.position?.y || doorYStart

  updateParam(model, 'door_glass_panel', {
    position: { x: positions.doorSection + frameThickness, y: preservedDoorGlassPanelY ?? doorYStart, z: 0 },
    width: sections.doorSection - frameThickness * 2,
    height: currentDoorGlassHeight,
  })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validated = validateDimensions(numValue, model.height as number)
  const finalWidth = validated.width

  const prevTotalWidth = (model.width as number) || 2000
  const widthDifference = finalWidth - prevTotalWidth

  const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const rightBottomFrame = getParam(model.parameters!, 'bottom_right_frame_profile')
  const currentLeftWidth = leftBottomFrame?.width || 550
  const currentRightWidth = rightBottomFrame?.width || 550

  const leftIncrease = Math.floor(widthDifference / 2)
  const rightIncrease = widthDifference - leftIncrease

  const newLeftWidth = currentLeftWidth + leftIncrease
  const newRightWidth = currentRightWidth + rightIncrease
  const frameThickness = 60

  if (newLeftWidth < 200 || newRightWidth < 200) {
    return model
  }

  model.width = finalWidth
  updateParam(model, 'bottom_left_frame_profile', { width: newLeftWidth })
  updateParam(model, 'bottom_right_frame_profile', { width: newRightWidth })

  const leftFixedGlassPanel = getParam(model.parameters!, 'left_glass_panel')
  if (leftFixedGlassPanel) {
    updateParam(model, 'left_glass_panel', { width: newLeftWidth - frameThickness * 2 })
  }

  if (cascadeUpdates) {
    const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
    const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
    const door1TopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
    const doorGlassPanel = getParam(model.parameters!, 'door_glass_panel')

    const preservedDoorGlassHeight = doorGlassPanel?.height
    const preservedDoor1LeftHeight = door1LeftFrame?.height
    const preservedDoor1RightHeight = door1RightFrame?.height
    const preservedDoorTopFrameHeight = door1TopFrame?.height
    const preservedDoorBottomFrameHeight = door1BottomFrame?.height

    const componentsToShift = [
      'door_1_bottom_frame_profile', 'door_1_left_frame_profile', 'door_1_right_frame_profile',
      'door_1_top_frame_profile', 'door_glass_panel', 'right_vertical_frame_profile',
      'right_glass_panel', 'bottom_right_frame_profile', 'door_top_glass_panel',
      'middle_horizontal_frame_profile', 'right_frame_profile',
    ]

    const leftVerticalFrame = getParam(model.parameters!, 'left_vertical_frame_profile')
    if (leftVerticalFrame) {
      updateParam(model, 'left_vertical_frame_profile', {
        position: { x: newLeftWidth - frameThickness, y: leftVerticalFrame.position?.y || frameThickness, z: leftVerticalFrame.position?.z || 0 },
      })
    }

    shiftComponentsRight(model, componentsToShift, leftIncrease)

    if (preservedDoorGlassHeight !== undefined) updateParam(model, 'door_glass_panel', { height: preservedDoorGlassHeight })
    if (preservedDoor1LeftHeight !== undefined) updateParam(model, 'door_1_left_frame_profile', { height: preservedDoor1LeftHeight })
    if (preservedDoor1RightHeight !== undefined) updateParam(model, 'door_1_right_frame_profile', { height: preservedDoor1RightHeight })
    if (preservedDoorTopFrameHeight !== undefined) updateParam(model, 'door_1_top_frame_profile', { height: preservedDoorTopFrameHeight })
    if (preservedDoorBottomFrameHeight !== undefined) updateParam(model, 'door_1_bottom_frame_profile', { height: preservedDoorBottomFrameHeight })

    const rightFixedGlassPanel = getParam(model.parameters!, 'right_glass_panel')
    if (rightFixedGlassPanel) {
      updateParam(model, 'right_glass_panel', { width: newRightWidth - frameThickness * 2 })
    }

    updateParam(model, 'top_frame_profile', { width: finalWidth })
    updateParam(model, 'right_frame_profile', { position: { x: finalWidth - frameThickness, y: 0, z: 0 } })

    const currentDoorSectionWidth = getParam(model.parameters!, 'door_1_bottom_frame_profile')?.width || 900
    model.doorConfiguration = {
      ...model.doorConfiguration!,
      totalDoorWidth: finalWidth,
      individualDoorWidth: [currentDoorSectionWidth],
      leftFixedWidth: newLeftWidth,
      rightFixedWidth: newRightWidth,
    }
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validated = validateDimensions(model.width as number, numValue)
  const finalHeight = validated.height

  model.height = finalHeight
  updateParam(model, 'left_frame_profile', { height: finalHeight })
  updateParam(model, 'right_frame_profile', { height: finalHeight })

  if (cascadeUpdates) {
    const preservedPositionsX: Record<string, number> = {}
    const preservedWidths: Record<string, number> = {}
    model.parameters!.forEach((param) => {
      if (param?.position?.x !== undefined) preservedPositionsX[param.positionId!] = param.position.x
      if (param?.width !== undefined) preservedWidths[param.positionId!] = param.width
    })

    const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
    const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
    const preservedTransomHeight = doorTopGlassPanel?.height
    const preservedTransomY = doorTopGlassPanel?.position?.y
    const preservedMiddleFrameHeight = middleHorizontalFrame?.height
    const preservedMiddleFrameY = middleHorizontalFrame?.position?.y

    recalculatePositions(model, true)

    // Restore x positions and widths
    Object.keys(preservedPositionsX).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.position && preservedPositionsX[positionId] !== undefined) {
        updateParam(model, positionId, { position: { ...component.position, x: preservedPositionsX[positionId] } as ModelParameter['position'] })
      }
    })
    Object.keys(preservedWidths).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.width !== undefined && preservedWidths[positionId] !== undefined) {
        updateParam(model, positionId, { width: preservedWidths[positionId] })
      }
    })

    // Restore transom
    if (preservedTransomHeight !== undefined) {
      updateParam(model, 'door_top_glass_panel', {
        height: preservedTransomHeight,
        position: { ...getParam(model.parameters!, 'door_top_glass_panel')?.position, y: preservedTransomY ?? getParam(model.parameters!, 'door_top_glass_panel')?.position?.y } as ModelParameter['position'],
      })
    }
    if (preservedMiddleFrameHeight !== undefined) {
      updateParam(model, 'middle_horizontal_frame_profile', {
        height: preservedMiddleFrameHeight,
        position: { ...getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position, y: preservedMiddleFrameY ?? getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position?.y } as ModelParameter['position'],
      })
    }

    // Recalculate door frame heights
    const topFrame = getParam(model.parameters!, 'top_frame_profile')
    const frameHeight = topFrame?.height || 60
    const calculatedTransomHeight = frameHeight + (preservedTransomHeight || 200) + (preservedMiddleFrameHeight || 60)
    const doorMainPanelHeight = finalHeight - calculatedTransomHeight

    updateParam(model, 'door_1_left_frame_profile', { height: doorMainPanelHeight })
    updateParam(model, 'door_1_right_frame_profile', { height: doorMainPanelHeight })

    // Adjust hinges
    const updatedDoor1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
    if (updatedDoor1RightFrame?.hardware?.hinges) {
      const oldFrameHeight = updatedDoor1RightFrame.height || doorMainPanelHeight
      const updatedHinges = updatedDoor1RightFrame.hardware.hinges.map((hinge) => {
        const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
        const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
        const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
        let newY: number
        if (isBottomHinge1) newY = Math.round(doorMainPanelHeight * 0.75)
        else if (isBottomHinge2) newY = Math.round(doorMainPanelHeight * 0.85)
        else newY = Math.max(60, Math.min(doorMainPanelHeight - (hinge.height || 100) - 60, relativePosition * doorMainPanelHeight))
        return { ...hinge, position: { ...hinge.position, y: newY } }
      })
      updateParam(model, 'door_1_right_frame_profile', { hardware: { ...updatedDoor1RightFrame.hardware, hinges: updatedHinges } })
    }

    // Calculate door glass panel height
    const updatedDoorTopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
    const updatedDoorBottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    const doorGlassPanelHeight = doorMainPanelHeight - (updatedDoorTopFrame?.height || 60) - (updatedDoorBottomFrame?.height || 60)
    const doorGlassPanel = getParam(model.parameters!, 'door_glass_panel')
    const oldHeight = doorGlassPanel?.height || 1760

    updateParam(model, 'door_glass_panel', { height: doorGlassPanelHeight })

    // Update handles proportionally
    const updatedDoorGlassPanel = getParam(model.parameters!, 'door_glass_panel')
    if (updatedDoorGlassPanel?.hardware?.handles) {
      const updatedHandles = updatedDoorGlassPanel.hardware.handles.map((handle) => {
        const relativeY = handle.position.y / oldHeight
        const newY = Math.max(50, Math.min(doorGlassPanelHeight - (handle.height || 200) - 50, relativeY * doorGlassPanelHeight))
        return { ...handle, position: { ...handle.position, y: newY } }
      })
      updateParam(model, 'door_glass_panel', { hardware: { ...updatedDoorGlassPanel.hardware, handles: updatedHandles } })
    }
  }

  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedHeight = Math.max(150, Math.min(800, numValue))
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const frameHeight = topFrame?.height || 60
  const transomeBottomFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
  const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')

  const currentTransomGlassHeight = doorTopGlassPanel?.height || 200
  const currentTransomBottomFrameHeight = transomeBottomFrame?.height || 60
  const currentTotalTransomHeight = frameHeight + currentTransomGlassHeight + currentTransomBottomFrameHeight
  const heightDifference = clampedHeight - currentTotalTransomHeight

  const newTransomGlassHeight = clampedHeight - frameHeight - currentTransomBottomFrameHeight
  updateParam(model, 'door_top_glass_panel', { height: newTransomGlassHeight })

  if (cascadeUpdates) {
    const preservedPositionsX: Record<string, number> = {}
    const preservedWidths: Record<string, number> = {}
    const preservedTotalHeight = model.height as number
    model.parameters!.forEach((param) => {
      if (param?.position?.x !== undefined) preservedPositionsX[param.positionId!] = param.position.x
      if (param?.width !== undefined) preservedWidths[param.positionId!] = param.width
    })

    const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
    const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
    const door1TopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
    const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    const doorGlassPanel = getParam(model.parameters!, 'door_glass_panel')
    const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')

    if (door1LeftFrame?.position) {
      updateParam(model, 'door_1_left_frame_profile', {
        position: { ...door1LeftFrame.position, y: (door1LeftFrame.position.y || 0) + heightDifference } as ModelParameter['position'],
        height: (door1LeftFrame.height || 0) - heightDifference,
      })
    }
    if (door1RightFrame?.position) {
      updateParam(model, 'door_1_right_frame_profile', {
        position: { ...door1RightFrame.position, y: (door1RightFrame.position.y || 0) + heightDifference } as ModelParameter['position'],
        height: (door1RightFrame.height || 0) - heightDifference,
      })
    }
    if (door1TopFrame?.position) {
      updateParam(model, 'door_1_top_frame_profile', {
        position: { ...door1TopFrame.position, y: (door1TopFrame.position.y || 0) + heightDifference } as ModelParameter['position'],
      })
    }
    if (middleHorizontalFrame?.position) {
      updateParam(model, 'middle_horizontal_frame_profile', {
        position: { ...middleHorizontalFrame.position, y: (middleHorizontalFrame.position.y || 0) + heightDifference } as ModelParameter['position'],
      })
    }
    if (door1BottomFrame?.position) {
      updateParam(model, 'door_1_bottom_frame_profile', {
        position: { ...door1BottomFrame.position, y: (door1BottomFrame.position.y || 0) + heightDifference } as ModelParameter['position'],
      })
    }
    if (doorGlassPanel?.position) {
      updateParam(model, 'door_glass_panel', {
        position: { ...doorGlassPanel.position, y: (doorGlassPanel.position.y || 0) + heightDifference } as ModelParameter['position'],
        height: (doorGlassPanel.height || 0) - heightDifference,
      })
    }

    // Restore x positions and widths
    Object.keys(preservedPositionsX).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.position && preservedPositionsX[positionId] !== undefined) {
        updateParam(model, positionId, { position: { ...component.position, x: preservedPositionsX[positionId] } as ModelParameter['position'] })
      }
    })
    Object.keys(preservedWidths).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.width !== undefined && preservedWidths[positionId] !== undefined) {
        updateParam(model, positionId, { width: preservedWidths[positionId] })
      }
    })

    model.height = preservedTotalHeight
    updateParam(model, 'left_frame_profile', { height: preservedTotalHeight })
    updateParam(model, 'right_frame_profile', { height: preservedTotalHeight })

    // Update handles proportionally
    const updatedDoor = getParam(model.parameters!, 'door_glass_panel')
    if (updatedDoor?.hardware?.handles && doorGlassPanel) {
      const oldHeight = doorGlassPanel.height || 1880
      const newHeight = updatedDoor.height || oldHeight
      const updatedHandles = updatedDoor.hardware.handles.map((handle) => {
        const relativeY = handle.position.y / oldHeight
        const newY = Math.max(50, Math.min(newHeight - (handle.height || 200) - 50, relativeY * newHeight))
        return { ...handle, position: { ...handle.position, y: newY } }
      })
      updateParam(model, 'door_glass_panel', { hardware: { ...updatedDoor.hardware, handles: updatedHandles } })
    }
  }

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedHeight = Math.max(1200, Math.min(3000, numValue))
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const frameHeight = topFrame?.height || 60
  const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
  const doorGlassPanel = getParam(model.parameters!, 'door_glass_panel')
  const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
  const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')

  const preservedTransomHeight = doorTopGlassPanel?.height
  const preservedTransomY = doorTopGlassPanel?.position?.y
  const preservedMiddleFrameHeight = middleHorizontalFrame?.height
  const preservedMiddleFrameY = middleHorizontalFrame?.position?.y

  const doorTopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
  const doorBottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const doorTopFrameHeight = doorTopFrame?.height || 60
  const doorBottomFrameHeight = doorBottomFrame?.height || 60

  const currentDoorMainPanelHeight = (doorGlassPanel?.height || 1760) + doorTopFrameHeight + doorBottomFrameHeight
  const doorGlassPanelHeight = clampedHeight - doorTopFrameHeight - doorBottomFrameHeight

  updateParam(model, 'door_glass_panel', { height: doorGlassPanelHeight })

  if (cascadeUpdates) {
    const preservedPositionsX: Record<string, number> = {}
    const preservedWidths: Record<string, number> = {}
    model.parameters!.forEach((param) => {
      if (param?.position?.x !== undefined) preservedPositionsX[param.positionId!] = param.position.x
      if (param?.width !== undefined) preservedWidths[param.positionId!] = param.width
    })

    if (door1LeftFrame) updateParam(model, 'door_1_left_frame_profile', { height: clampedHeight })
    if (door1RightFrame) updateParam(model, 'door_1_right_frame_profile', { height: clampedHeight })

    // Adjust hinges
    const updatedDoor1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
    if (updatedDoor1RightFrame?.hardware?.hinges) {
      const oldFrameHeight = door1RightFrame?.height || clampedHeight
      const updatedHinges = updatedDoor1RightFrame.hardware.hinges.map((hinge) => {
        const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
        const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
        const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
        let newY: number
        if (isBottomHinge1) newY = Math.round(clampedHeight * 0.75)
        else if (isBottomHinge2) newY = Math.round(clampedHeight * 0.85)
        else newY = Math.max(60, Math.min(clampedHeight - (hinge.height || 100) - 60, relativePosition * clampedHeight))
        return { ...hinge, position: { ...hinge.position, y: newY } }
      })
      updateParam(model, 'door_1_right_frame_profile', { hardware: { ...updatedDoor1RightFrame.hardware, hinges: updatedHinges } })
    }

    const transomH = (topFrame?.height || 60) + (preservedTransomHeight || 200) + (preservedMiddleFrameHeight || 60)
    model.height = transomH + clampedHeight

    updateParam(model, 'left_frame_profile', { height: model.height as number })
    updateParam(model, 'right_frame_profile', { height: model.height as number })

    const verticalFrameHeight = (model.height as number) - frameHeight
    const leftVerticalFrame = getParam(model.parameters!, 'left_vertical_frame_profile')
    if (leftVerticalFrame?.height !== undefined) updateParam(model, 'left_vertical_frame_profile', { height: verticalFrameHeight })
    const rightVerticalFrame = getParam(model.parameters!, 'right_vertical_frame_profile')
    if (rightVerticalFrame?.height !== undefined) updateParam(model, 'right_vertical_frame_profile', { height: verticalFrameHeight })

    const fixedPanelHeight = (model.height as number) - frameHeight * 2
    const leftFixedGlassPanel = getParam(model.parameters!, 'left_glass_panel')
    if (leftFixedGlassPanel?.height !== undefined) updateParam(model, 'left_glass_panel', { height: fixedPanelHeight })
    const rightFixedGlassPanel = getParam(model.parameters!, 'right_glass_panel')
    if (rightFixedGlassPanel?.height !== undefined) updateParam(model, 'right_glass_panel', { height: fixedPanelHeight })

    const bottomY = (model.height as number) - frameHeight
    const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
    if (leftBottomFrame?.position) updateParam(model, 'bottom_left_frame_profile', { position: { ...leftBottomFrame.position, y: bottomY } as ModelParameter['position'] })
    const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    if (door1BottomFrame?.position) updateParam(model, 'door_1_bottom_frame_profile', { position: { ...door1BottomFrame.position, y: bottomY } as ModelParameter['position'] })
    const rightBottomFrame = getParam(model.parameters!, 'bottom_right_frame_profile')
    if (rightBottomFrame?.position) updateParam(model, 'bottom_right_frame_profile', { position: { ...rightBottomFrame.position, y: bottomY } as ModelParameter['position'] })

    // Restore x positions and widths
    Object.keys(preservedPositionsX).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.position && preservedPositionsX[positionId] !== undefined) {
        updateParam(model, positionId, { position: { ...component.position, x: preservedPositionsX[positionId] } as ModelParameter['position'] })
      }
    })
    Object.keys(preservedWidths).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.width !== undefined && preservedWidths[positionId] !== undefined) {
        updateParam(model, positionId, { width: preservedWidths[positionId] })
      }
    })

    // Restore transom
    if (preservedTransomHeight !== undefined) {
      updateParam(model, 'door_top_glass_panel', {
        height: preservedTransomHeight,
        position: { ...getParam(model.parameters!, 'door_top_glass_panel')?.position, y: preservedTransomY ?? getParam(model.parameters!, 'door_top_glass_panel')?.position?.y } as ModelParameter['position'],
      })
    }
    if (preservedMiddleFrameHeight !== undefined) {
      updateParam(model, 'middle_horizontal_frame_profile', {
        height: preservedMiddleFrameHeight,
        position: { ...getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position, y: preservedMiddleFrameY ?? getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position?.y } as ModelParameter['position'],
      })
    }

    updateDoorHandles(model, 'door_glass_panel', clampedHeight)
  }

  return model
}

const leftSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const frameThickness = 60
  const minWidth = 200
  const minRightWidth = 200

  const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const rightBottomFrame = getParam(model.parameters!, 'bottom_right_frame_profile')

  const currentLeftWidth = leftBottomFrame?.width || 550
  const doorSectionWidth = door1BottomFrame?.width || 900
  const rightFixedWidth = rightBottomFrame?.width || 550

  const currentTotalWidth = (model.width as number) || 2000
  const maxWidth = Math.max(minWidth, currentTotalWidth - doorSectionWidth - minRightWidth)
  const clampedWidth = Math.max(minWidth, Math.min(maxWidth, numValue))

  const newTotalWidth = clampedWidth + doorSectionWidth + rightFixedWidth
  const validated = validateDimensions(newTotalWidth, model.height as number)
  const finalTotalWidth = validated.width

  model.width = finalTotalWidth
  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })
  updateParam(model, 'bottom_left_frame_profile', { width: clampedWidth })
  updateParam(model, 'left_glass_panel', { width: clampedWidth - frameThickness * 2 })

  if (cascadeUpdates) {
    const widthDifference = clampedWidth - currentLeftWidth
    const componentsToShift = [
      'door_1_bottom_frame_profile', 'door_1_left_frame_profile', 'door_1_right_frame_profile',
      'door_1_top_frame_profile', 'door_glass_panel', 'right_vertical_frame_profile',
      'right_glass_panel', 'bottom_right_frame_profile', 'door_top_glass_panel',
      'middle_horizontal_frame_profile', 'right_frame_profile',
    ]

    const leftVerticalFrame = getParam(model.parameters!, 'left_vertical_frame_profile')
    if (leftVerticalFrame) {
      updateParam(model, 'left_vertical_frame_profile', {
        position: { x: clampedWidth - frameThickness, y: leftVerticalFrame.position?.y || frameThickness, z: leftVerticalFrame.position?.z || 0 },
      })
    }

    shiftComponentsRight(model, componentsToShift, widthDifference)
    updateParam(model, 'right_frame_profile', { position: { x: finalTotalWidth - frameThickness, y: 0, z: 0 } })

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: finalTotalWidth, leftFixedWidth: clampedWidth }
  }

  return model
}

const doorSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedWidth = Math.max(600, Math.min(1500, numValue))
  const frameThickness = 60

  const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const rightBottomFrame = getParam(model.parameters!, 'bottom_right_frame_profile')

  const currentLeftWidth = leftBottomFrame?.width || 550
  const currentDoorSectionWidth = door1BottomFrame?.width || 900
  const rightFixedWidth = rightBottomFrame?.width || 550

  const newTotalWidth = currentLeftWidth + clampedWidth + rightFixedWidth
  const validated = validateDimensions(newTotalWidth, model.height as number)
  const finalTotalWidth = validated.width

  model.width = finalTotalWidth
  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })
  updateParam(model, 'door_1_bottom_frame_profile', { width: clampedWidth })
  updateParam(model, 'door_1_top_frame_profile', { width: clampedWidth })
  updateParam(model, 'middle_horizontal_frame_profile', { width: clampedWidth })
  updateParam(model, 'door_top_glass_panel', { width: clampedWidth })
  updateParam(model, 'door_glass_panel', { width: clampedWidth - frameThickness * 2 })

  if (cascadeUpdates) {
    const widthDifference = clampedWidth - currentDoorSectionWidth
    const componentsToShift = ['right_glass_panel', 'bottom_right_frame_profile', 'right_frame_profile']

    const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
    if (door1LeftFrame) {
      updateParam(model, 'door_1_left_frame_profile', {
        position: { x: currentLeftWidth, y: door1LeftFrame.position?.y || 320, z: door1LeftFrame.position?.z || 0 },
      })
    }

    const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
    if (door1RightFrame) {
      updateParam(model, 'door_1_right_frame_profile', {
        position: { x: currentLeftWidth + clampedWidth - frameThickness, y: door1RightFrame.position?.y || 320, z: door1RightFrame.position?.z || 0 },
      })
    }

    const rightVerticalFrame = getParam(model.parameters!, 'right_vertical_frame_profile')
    if (rightVerticalFrame) {
      updateParam(model, 'right_vertical_frame_profile', {
        position: { x: currentLeftWidth + clampedWidth, y: rightVerticalFrame.position?.y || frameThickness, z: rightVerticalFrame.position?.z || 0 },
      })
    }

    shiftComponentsRight(model, componentsToShift, widthDifference)
    updateParam(model, 'right_frame_profile', { position: { x: finalTotalWidth - frameThickness, y: 0, z: 0 } })

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: finalTotalWidth, individualDoorWidth: [clampedWidth], centerDoorWidth: clampedWidth }
  }

  return model
}

const rightSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const frameThickness = 60
  const minWidth = 200
  const minLeftWidth = 200

  const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const rightBottomFrame = getParam(model.parameters!, 'bottom_right_frame_profile')

  const currentLeftWidth = leftBottomFrame?.width || 550
  const doorSectionW = door1BottomFrame?.width || 900
  const currentRightWidth = rightBottomFrame?.width || 550

  const currentTotalWidth = (model.width as number) || 2000
  const maxWidth = Math.max(minWidth, currentTotalWidth - doorSectionW - minLeftWidth)
  const clampedWidth = Math.max(minWidth, Math.min(maxWidth, numValue))

  const newTotalWidth = currentLeftWidth + doorSectionW + clampedWidth
  const validated = validateDimensions(newTotalWidth, model.height as number)
  const finalTotalWidth = validated.width

  model.width = finalTotalWidth
  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })
  updateParam(model, 'bottom_right_frame_profile', { width: clampedWidth })
  updateParam(model, 'right_glass_panel', { width: clampedWidth - frameThickness * 2 })

  if (cascadeUpdates) {
    updateParam(model, 'right_frame_profile', { position: { x: finalTotalWidth - frameThickness, y: 0, z: 0 } })

    const rightVerticalFrame = getParam(model.parameters!, 'right_vertical_frame_profile')
    if (rightVerticalFrame) {
      updateParam(model, 'right_vertical_frame_profile', {
        position: { x: currentLeftWidth + doorSectionW, y: rightVerticalFrame.position?.y || frameThickness, z: rightVerticalFrame.position?.z || 0 },
      })
    }

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: finalTotalWidth, rightFixedWidth: clampedWidth }
  }

  return model
}

export const model13DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  transomHeight,
  doorPanelHeight,
  leftSectionWidth,
  doorSectionWidth,
  rightSectionWidth,
}
